import { motion, useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { Send, Mail, User, MessageSquare, AlertCircle, CheckCircle, Github, Linkedin, Instagram, Video } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { SOCIAL_LINKS } from '../../lib/constants';
import toast, { Toaster } from 'react-hot-toast';

export default function ContactSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    honeypot: '', // Anti-spam
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Honeypot check
    if (formData.honeypot) {
      return; // Silent fail for bots
    }

    setLoading(true);

    try {
      const { error } = await supabase.from('contacts').insert([
        {
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
          status: 'unread',
        },
      ]);

      if (error) throw error;

      toast.custom((t) => (
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 50 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8 }}
          className="glass-card p-4 flex items-center gap-3"
        >
          <CheckCircle className="text-neon-green w-6 h-6" />
          <div>
            <p className="font-bold text-white">Pesan Terkirim!</p>
            <p className="text-sm text-gray-400">Terima kasih, saya akan segera merespons.</p>
          </div>
        </motion.div>
      ));

      // Reset form
      setFormData({ name: '', email: '', subject: '', message: '', honeypot: '' });
    } catch (error: any) {
      toast.custom((t) => (
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 50 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8 }}
          className="glass-card p-4 flex items-center gap-3 border border-red-500/50"
        >
          <AlertCircle className="text-red-500 w-6 h-6" />
          <div>
            <p className="font-bold text-white">Gagal Mengirim</p>
            <p className="text-sm text-gray-400">{error.message}</p>
          </div>
        </motion.div>
      ));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" ref={ref} className="py-20 bg-dark-lighter/30">
      <Toaster position="top-right" />
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="neon-text-blue">Hubungi</span> <span className="text-white">Saya</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-neon-blue to-neon-green mx-auto rounded-full"></div>
          <p className="text-gray-400 mt-4">
            Punya projek atau pertanyaan? Mari berdiskusi!
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-5xl mx-auto">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-6"
          >
            <div className="glass-card p-8">
              <h3 className="text-2xl font-bold text-white mb-6">Informasi Kontak</h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-neon-blue/10">
                    <Mail className="w-6 h-6 text-neon-blue" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Email</p>
                    <p className="text-white font-semibold">nawawnawawimahinutsman@gmail.com</p>
                  </div>
                </div>
              </div>
            </div>

            {/* WhatsApp CTA */}
            <motion.a
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              href={SOCIAL_LINKS.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="block glass-card p-8 text-center group cursor-pointer"
            >
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-neon-green/10 flex items-center justify-center group-hover:bg-neon-green/20 transition">
                <MessageSquare className="w-8 h-8 text-neon-green" />
              </div>
              <h4 className="text-xl font-bold text-white mb-2">WhatsApp Direct</h4>
              <p className="text-gray-400 text-sm mb-4">
                Respon lebih cepat via WhatsApp
              </p>
              <span className="btn-neon-green inline-block">
                Chat Sekarang
              </span>
            </motion.a>

            {/* Social Media Links */}
            <div className="glass-card p-8">
              <h4 className="text-xl font-bold text-white mb-4 text-center">Follow Social Media</h4>
              <div className="flex justify-center gap-4">
                <a href={SOCIAL_LINKS.github} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-neon-blue transition">
                  <Github size={24} />
                </a>
                <a href={SOCIAL_LINKS.linkedin} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-neon-blue transition">
                  <Linkedin size={24} />
                </a>
                <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-neon-blue transition">
                  <Instagram size={24} />
                </a>
                <a href={SOCIAL_LINKS.tiktok} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-neon-blue transition">
                  <Video size={24} />
                </a>
                <a href={SOCIAL_LINKS.whatsapp} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-neon-green transition">
                  <MessageSquare size={24} />
                </a>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <form onSubmit={handleSubmit} className="glass-card p-8 space-y-5">
              {/* Honeypot */}
              <input
                type="text"
                name="honeypot"
                value={formData.honeypot}
                onChange={(e) => setFormData({ ...formData, honeypot: e.target.value })}
                className="hidden"
                tabIndex={-1}
                autoComplete="off"
              />

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  <User className="inline w-4 h-4 mr-2" />
                  Nama Lengkap
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 bg-dark-lighter border border-neon-blue/30 rounded-xl text-white focus:outline-none focus:border-neon-blue transition"
                  placeholder="Nama Anda"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  <Mail className="inline w-4 h-4 mr-2" />
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 bg-dark-lighter border border-neon-blue/30 rounded-xl text-white focus:outline-none focus:border-neon-blue transition"
                  placeholder="email@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Subjek
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="w-full px-4 py-3 bg-dark-lighter border border-neon-blue/30 rounded-xl text-white focus:outline-none focus:border-neon-blue transition"
                  placeholder="Subjek pesan (opsional)"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  <MessageSquare className="inline w-4 h-4 mr-2" />
                  Pesan
                </label>
                <textarea
                  required
                  rows={5}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-4 py-3 bg-dark-lighter border border-neon-blue/30 rounded-xl text-white focus:outline-none focus:border-neon-blue transition resize-none"
                  placeholder="Tulis pesan Anda di sini..."
                ></textarea>
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={loading}
                className="w-full btn-neon-green flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Mengirim...
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    Kirim Pesan
                  </>
                )}
              </motion.button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
